export const SLOT_GAME_SETTINGS_TOKEN = Symbol('SlotGameSettings');

export interface ISlotGameSettings {
  // Display name for the slot (used in HUDs)
  slotName?: string;
  paytable: {
    resourcePath: string;
    sections: number[][];
  };
  symbols: {
    simpleIcons: Record<string, string>;
    specialIcons: Record<string, string>;
    extraIcons: Record<string, string>;
    maxIconId: number;
  };
  cheat: {
    url: string;
  };
  paylines: {
    game: number[][];
    dev: number[][];
  } | null;
  config: {
    url: string;
    bonusPath?: string;
  };
}

export class SlotGameSettings {
  constructor(
    private resourceUrl: string,
    public settings: ISlotGameSettings
  ) {}

  public getSlotName(): string | null {
    return this.settings.slotName ?? null;
  }

  public getSymbolIconMap(): Record<string, string> {
    return {
      ...this.getSpecialSymbolIconMap(),
      ...this.getSimpleSymbolIconMap(),
      ...this.getExtraSymbolIconMap(),
    };
  }

  public getSpecialSymbolIconMap(): Record<string, string> {
    return Object.entries(this.settings.symbols.specialIcons).reduce(
      (acc, [key, value]) => {
        acc[key] = this.getFilePath(value);
        return acc;
      },
      {} as Record<string, string>
    );
  }

  public getSimpleSymbolIconMap(): Record<string, string> {
    return Object.entries(this.settings.symbols.simpleIcons).reduce(
      (acc, [key, value]) => {
        acc[key] = this.getFilePath(value);
        return acc;
      },
      {} as Record<string, string>
    );
  }

  public getExtraSymbolIconMap(): Record<string, string> {
    return Object.entries(this.settings.symbols.extraIcons).reduce(
      (acc, [key, value]) => {
        acc[key] = this.getFilePath(value);
        return acc;
      },
      {} as Record<string, string>
    );
  }

  public getPaytableSections(): number[][] {
    return this.settings.paytable.sections;
  }

  public getPaylines(): number[][] {
    if (!this.settings.paylines) {
      return [];
    }
    return this.settings.paylines.game;
  }

  public getPaytablePaylines(): number[][] {
    if (!this.settings.paylines) {
      return [];
    }
    return this.settings.paylines.dev;
  }

  public getMaxIconId(): number {
    return this.settings.symbols.maxIconId;
  }

  public getCheatUrl(): string {
    return this.settings.cheat.url;
  }

  public getFilePath(path: string): string {
    // If paytable resourcePath is absolute/static (e.g., 'assets/cheat' or '/assets/...'),
    // use it as-is to avoid prefixing with the language-specific resourceUrl.
    const resourcePath = this.settings.paytable.resourcePath;
    if (resourcePath.startsWith('/') || resourcePath.startsWith('assets/')) {
      return `${resourcePath}/${path}`;
    }
    return `${this.resourceUrl}/${resourcePath}/${path}`;
  }

  public getConfigUrl(): string {
    return `./${this.settings.config.url}`;
  }

  public getBonusConfigUrl(): string | null {
    return this.settings.config.bonusPath ? `./${this.settings.config.bonusPath}` : null;
  }
}
